import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from '../../config/axios';
import Button from '../common/Button';
import { PageLayout } from '../common/index.js';
import { MapPin, ArrowLeft, Package, Clock, User, Phone, Mail, FileText, Eye } from "lucide-react";
import DocumentModal from '../common/DocumentModal';

const UserShipmentTracking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shipmentId, setShipmentId] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returnId, setReturnId] = useState(null);
  const [loadingReturnId, setLoadingReturnId] = useState(false);
  const [deliveryImageModal, setDeliveryImageModal] = useState(false);
  const [selectedDeliveryImage, setSelectedDeliveryImage] = useState(null);

  // Fetch return ID when shipment status is "Returned"
  useEffect(() => {
    if (trackingData?.shipment_details?.status?.toLowerCase() === 'returned') {
      fetchReturnId(trackingData.shipment_details.shipment_id);
    }
  }, [trackingData]);

  const fetchReturnId = async (shipmentId) => {
    try {
      setLoadingReturnId(true);
      // Fetch all returns and filter by shipment_id
      const response = await axios.get('/shipments/returned', {
        params: { return_status: 'close' }
      });
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        // Get the most recent return for this shipment
        const returns = response.data.data.filter(r => r.shipment_id === shipmentId);
        if (returns.length > 0) {
          // Sort by created_at descending and get the first one
          const sortedReturns = returns.sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          );
          setReturnId(sortedReturns[0].return_id);
        }
      }
    } catch (err) {
      console.error('Error fetching return ID:', err);
      // Don't show error to user, just hide the button
      setReturnId(null);
    } finally {
      setLoadingReturnId(false);
    }
  };

  const handleViewDeliveryImage = (imageBase64) => {
    setSelectedDeliveryImage(imageBase64);
    setDeliveryImageModal(true);
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    
    if (!shipmentId.trim()) {
      setError('Please enter a shipment ID');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);
    setReturnId(null);

    try {
      const response = await axios.get(`/tracking/${shipmentId}`);
      if (response.data.success) {
        setTrackingData(response.data);
      } else {
        setError(response.data.error || 'Shipment not found');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tracking information');
    } finally {
      setLoading(false);
    }
  };

  const handleViewShipment = () => {
    if (trackingData?.shipment_details?.shipment_id) {
      navigate(`/shipments/view/${trackingData.shipment_details.shipment_id}`);
    }
  };

  const handleViewReturn = () => {
    if (returnId) {
      navigate(`/returns/view/${returnId}`);
    }
  };

  const getStatusBadgeColor = (status) => {
    const statusColors = {
      'booked': 'bg-blue-100 text-blue-800 border-blue-200',
      'dispatched': 'bg-purple-100 text-purple-800 border-purple-200',
      'received': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'delivered': 'bg-green-100 text-green-800 border-green-200',
      'returned': 'bg-red-100 text-red-800 border-red-200',
      'rejected': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPickupTypeBadgeColor = (type) => {
    const colors = {
      'pickup': 'bg-green-100 text-green-800 border-green-200',
      'parcel_shop': 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[type?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const isReturnedStatus = trackingData?.shipment_details?.status?.toLowerCase() === 'returned';

  return (
    <>
    <PageLayout
      title="Track Shipment"
      icon={Package}
      maxWidth="max-w-7xl"
      actions={
        <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
                {/* Search Section */}
                <div className="bg-white rounded-card shadow-card border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6">
                  <p className="text-sm sm:text-base text-secondary mb-4 font-medium">Enter shipment ID to get real-time tracking updates</p>
                  <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={shipmentId}
                      onChange={(e) => setShipmentId(e.target.value)}
                      placeholder="Enter Shipment ID (e.g., 001234)"
                      className="input-clean flex-1"
                    />
                    <Button type="submit" variant="primary" size="md" loading={loading} className="shrink-0">
                      {loading ? 'Tracking...' : 'Track Shipment'}
                    </Button>
                  </form>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-card p-3 sm:p-4 mb-4 sm:mb-6 text-red-800 font-medium text-sm sm:text-base">
                    ⚠️ {error}
                  </div>
                )}

                {/* Tracking Information */}
                {trackingData && (
                  <div className="space-y-4 sm:space-y-6">
                    {/* Shipment Details */}
                    <div className="bg-white rounded-card shadow-card border border-gray-100 p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100 flex-wrap gap-2">
                        <h2 className="text-base sm:text-lg font-semibold text-dark flex items-center gap-2">
                          <Package className="w-5 h-5 text-brand" />
                          Shipment Details
                        </h2>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="primary" size="sm" icon={Eye} onClick={handleViewShipment}>
                            View
                          </Button>
                          {isReturnedStatus && returnId && (
                            <Button variant="danger" size="sm" icon={Eye} onClick={handleViewReturn} disabled={loadingReturnId} loading={loadingReturnId}>
                              {loadingReturnId ? 'Loading...' : 'Return'}
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
                        <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                          <label className="block text-xs font-medium text-secondary mb-0.5">Shipment ID</label>
                          <p className="font-medium text-tertiary text-xs sm:text-sm">
                            #{trackingData.shipment_details.shipment_id}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                          <label className="block text-xs font-medium text-secondary mb-0.5">Status</label>
                          <span className="font-medium text-tertiary text-xs sm:text-sm">
                            {trackingData.shipment_details.status}
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                          <label className="block text-xs font-medium text-secondary mb-0.5">Articles</label>
                          <p className="font-medium text-tertiary text-xs sm:text-sm">
                            {trackingData.shipment_details.no_of_articles || 0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                          <label className="block text-xs font-medium text-secondary mb-0.5">Weight</label>
                          <p className="font-medium text-tertiary text-xs sm:text-sm">{trackingData.shipment_details.weight || 'N/A'} kg</p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                          <label className="block text-xs font-medium text-secondary mb-0.5">Vehicle</label>
                          <p className="font-medium text-tertiary text-xs sm:text-sm line-clamp-1">{trackingData.shipment_details.vehicle_number || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                          <label className="block text-xs font-medium text-secondary mb-0.5">Created</label>
                          <p className="font-medium text-tertiary text-xs sm:text-sm">
                            {new Date(trackingData.shipment_details.created_at).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Sender & Receiver Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 mt-3 pt-3 border-t-2 border-gray-200">
                        <div className="bg-brand-light rounded p-2 sm:p-3 border border-blue-100">
                          <h3 className="font-semibold text-gray-900 mb-1 text-xs sm:text-sm flex items-center gap-1">Sender</h3>
                          <p className="text-xs text-gray-700 mb-0.5">
                            <span className="font-semibold">Name:</span> {trackingData.shipment_details.sender_name}
                          </p>
                          <p className="text-xs text-gray-700 line-clamp-2">
                            <span className="font-semibold">Address:</span> {trackingData.shipment_details.sender_address}
                          </p>
                        </div>
                        <div className="bg-green-50 rounded p-2 sm:p-3 border border-green-100">
                          <h3 className="font-semibold text-gray-900 mb-1 text-xs sm:text-sm flex items-center gap-1">Receiver</h3>
                          <p className="text-xs text-gray-700 mb-0.5">
                            <span className="font-semibold">Name:</span> {trackingData.shipment_details.receiver_name}
                          </p>
                          <p className="text-xs text-gray-700 line-clamp-2">
                            <span className="font-semibold">Address:</span> {trackingData.shipment_details.receiver_address}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Current Location */}
                    {trackingData.current_location && (
                      <div className="bg-white rounded-card shadow-card border border-gray-100 p-4 sm:p-6">
                        <h2 className="text-base sm:text-lg font-semibold mb-3 pb-3 flex items-center gap-2 border-b border-gray-100 text-dark">
                           <MapPin className="w-5 h-5 text-secondary" /> Current Location
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                          <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                            <p className="block text-xs font-medium text-secondary mb-0.5">Location</p>
                            <p className="font-medium text-tertiary text-xs sm:text-sm">{trackingData.current_location.location}</p>
                          </div>
                          <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                            <p className="block text-xs font-medium text-secondary mb-0.5">Status</p>
                            <p className="font-medium text-tertiary text-xs sm:text-sm">{trackingData.current_location.status}</p>
                          </div>
                          <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                            <p className="block text-xs font-medium text-secondary mb-0.5">Updated By</p>
                            <p className="font-medium text-tertiary text-xs sm:text-sm line-clamp-1">{trackingData.current_location.updated_by}</p>
                          </div>
                          <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
                            <p className="block text-xs font-medium text-secondary mb-0.5">Last Updated</p>
                            <p className="font-medium text-tertiary text-xs sm:text-sm">
                              {new Date(trackingData.current_location.timestamp).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tracking Timeline */}
                    {trackingData.tracking_timeline && trackingData.tracking_timeline.length > 0 && (
                      <div className="bg-white rounded-card shadow-card border border-gray-100 p-4 sm:p-6">
                        <style>{`
                          @keyframes slideInDown {
                            from {
                              opacity: 0;
                              transform: translateY(-10px);
                            }
                            to {
                              opacity: 1;
                              transform: translateY(0);
                            }
                          }
                          .timeline-item {
                            animation: slideInDown 0.5s ease-out forwards;
                          }
                          .timeline-item:nth-child(1) { animation-delay: 0.1s; }
                          .timeline-item:nth-child(2) { animation-delay: 0.2s; }
                          .timeline-item:nth-child(3) { animation-delay: 0.3s; }
                          .timeline-item:nth-child(4) { animation-delay: 0.4s; }
                          .timeline-item:nth-child(5) { animation-delay: 0.5s; }
                        `}</style>
                        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 pb-3 flex items-center gap-2 border-b border-gray-100 text-dark">
                          <Clock className="w-5 h-5 text-brand" /> Tracking Timeline
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-1 gap-2 sm:gap-3">
                          {trackingData.tracking_timeline.map((entry, index) => (
                            <div key={index} className="timeline-item">

                              {/* Timeline Content */}
                              <div className="pb-3 sm:pb-4 flex-1 p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <h3 className="font-semibold text-tertiary text-sm sm:text-base mb-2">{entry.status}</h3>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                                  <p className="text-gray-700 flex items-center gap-1">
                                    <span className="font-semibold flex items-center gap-0.5 flex-shrink-0">
                                      <MapPin className="w-3 h-3 text-secondary" />
                                      Location:
                                    </span>
                                    <span className="break-words">{entry.location}</span>
                                  </p>
                                  
                                  <p className="text-gray-700 flex items-center gap-1">
                                    <span className="font-semibold flex items-center gap-0.5 flex-shrink-0">
                                      <Clock className="w-3 h-3 text-secondary" />
                                      Time:
                                    </span>
                                    <span className="break-words">
                                      {new Date(entry.timestamp).toLocaleString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </p>
                                  
                                  <p className="text-gray-700 flex items-center gap-1">
                                    <span className="font-semibold flex items-center gap-0.5 flex-shrink-0">
                                      <User className="w-3 h-3 text-secondary" />
                                      Updated by:
                                    </span>
                                    <span className="break-words line-clamp-1">
                                      {entry.updated_by_name}
                                    </span>
                                  </p>
                                  
                                  <p className="text-gray-700 flex items-center gap-1">
                                    <span className="font-semibold flex items-center gap-0.5 flex-shrink-0">
                                      <Phone className="w-3 h-3 text-secondary" />
                                      Contact:
                                    </span>
                                    <a
                                      href={`tel:${entry.updated_by_contact}`}
                                      className="text-tertiary hover:underline break-all line-clamp-1"
                                    >
                                      {entry.updated_by_contact}
                                    </a>
                                  </p>
                                  
                                  <p className="text-gray-700 flex items-center gap-1">
                                    <span className="font-semibold flex items-center gap-0.5 flex-shrink-0">
                                      <Mail className="w-3 h-3 text-secondary" />
                                      Email:
                                    </span>
                                    <a
                                      href={`mailto:${entry.updated_by_email}`}
                                      className="text-tertiary hover:underline break-all line-clamp-1"
                                    >
                                      {entry.updated_by_email}
                                    </a>
                                  </p>
                                  
                                  {entry.status?.toLowerCase() === 'delivered' && entry.delivery_receiver && (
                                    <p className="text-gray-700 flex items-center gap-1">
                                      <span className="font-semibold flex items-center gap-0.5 flex-shrink-0">
                                        <User className="w-3 h-3 text-secondary" />
                                        Delivered To:
                                      </span>
                                      <span className="break-words line-clamp-1">{entry.delivery_receiver}</span>
                                    </p>
                                  )}
                                  
                                  {entry.status?.toLowerCase() === 'delivered' && entry.delivery_receiver_image_base64 && (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        icon={Eye}
                                        onClick={() => handleViewDeliveryImage(entry.delivery_receiver_image_base64)}
                                      >
                                        Photo
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                {entry.remarks && (
                                  <p className="text-xs sm:text-sm text-gray-600 mt-2 flex items-start gap-1">
                                    <span className="font-semibold flex items-center gap-0.5 flex-shrink-0">
                                      <FileText className="w-3 h-3 text-secondary" />
                                      Note:
                                    </span>
                                    <span className="break-words line-clamp-2">
                                      {entry.remarks}
                                    </span>
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* No Shipment Selected */}
                {!trackingData && !loading && !error && (
                  <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 sm:p-8 text-center">
                    <div className="mb-2"></div>
                    <p className="text-gray-600 text-sm sm:text-base font-medium">
                      Enter a shipment ID above to view tracking information
                    </p>
                  </div>
                )}
    </PageLayout>
      {/* Delivery Receiver Image Modal */}
      <DocumentModal
        isOpen={deliveryImageModal}
        onClose={() => setDeliveryImageModal(false)}
        documentUrl={selectedDeliveryImage ? `data:image/jpeg;base64,${selectedDeliveryImage}` : ''}
        documentType="Delivery Receiver Photo"
      />
    </>
  );
};

export default UserShipmentTracking;